import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCourses } from "../../utils/wellspoken.api";

export default function CoursesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Blue gradient for Enroll button
  const glossyRedButton =
    "relative rounded-full text-white font-semibold \
   bg-[linear-gradient(to_bottom,#b30000_0%,#ff3b3b_45%,#d10000_55%,#7a0000_100%)] \
   shadow-[0_8px_18px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.35)] \
   transition-all duration-300 ease-out \
   hover:brightness-110 hover:-translate-y-[2px] \
   hover:shadow-[0_12px_28px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.45)] \
   active:translate-y-[1px] active:shadow-[0_6px_12px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.25)]";


  useEffect(() => {
    let isMounted = true;

    fetchCourses()
      .then((data) => {
        if (isMounted) {
          setCourses(data || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError("Unable to load courses at the moment.");
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleEnroll = (course) => {
    navigate("/contact", {
      state: { type: course.courseName },
    });
  };

  return (
    <main aria-labelledby="courses-heading">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden pt-16 pb-15 bg-surface">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(99,102,241,0.10),transparent_45%)]"
        />
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <h1
            id="courses-heading"
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight"
          >
            Master English with{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Our Courses
            </span>
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Structured, practical English courses designed to improve real-world communication, interviews, and career growth.
          </p>
        </div>
      </section>

      {/* ================= COURSES GRID ================= */}
      <section className="bg-surface py-10 lg:py-20 sm:py-12" aria-label="Available English courses">
        <div className="max-w-7xl mx-auto px-4">
          {loading && <p className="text-center text-gray-500">Loading courses…</p>}
          {error && <p className="text-center text-red-500">{error}</p>}

          {!loading && !error && (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {courses.map((course, index) => (
                <article
                  key={course.courseId}
                  className="group relative mb-5 bg-white border border-gray-200 rounded-[2rem] shadow-2xl flex flex-col transition-transform duration-300 hover:-translate-y-3 hover:shadow-blue-500/20"
                >
                  {/* Card Content */}
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    {/* Level Badge */}
                    <div className="inline-flex items-center gap-2 px-2 py-1.5  text-gray-800 text-xl font-semibold rounded-full mb-4 shadow-sm">
                      <span>🎓</span>
                      <span>{course.courseLevel}</span>
                    </div>

                    {/* Course Title */}
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                      {course.courseName}
                    </h2>

                    {/* Description */}
                    <ul className="space-y-2 mb-4 text-sm text-gray-700">
                      {Array.isArray(course.courseDescription)
                        ? course.courseDescription.map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="mt-1 h-2 w-2 rounded-full bg-blue-500" />
                            <span>{f}</span>
                          </li>
                        ))
                        : course.courseDescription
                          ?.split("\n")
                          .map((f, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="mt-1 h-2 w-2 rounded-full bg-blue-500" />
                              <span>{f.trim()}</span>
                            </li>
                          ))}
                    </ul>

                    {/* Meta Info */}
                    <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-blue-50 rounded-2xl border border-blue-100 text-center text-sm text-gray-700">
                      <div>
                        <p className="text-blue-600 font-bold">{course.courseDuration}</p>
                        <p className="uppercase">Months</p>
                      </div>
                      <div className="border-l border-blue-200">
                        <p className="text-gray-900 font-bold">{course.courseMode}</p>
                        <p className="uppercase">Mode</p>
                      </div>
                    </div>

                    {/* Features */}
                    {Array.isArray(course.items) && course.items.length > 0 && (
                      <div className="mb-4 space-y-2">
                        {course.items.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="flex gap-2 items-center text-sm text-gray-700 bg-blue-50 rounded-xl px-3 py-2">
                            <span className="text-blue-500 font-bold">→</span>
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="mt-auto pt-4 flex items-center justify-between gap-4 flex-wrap">
                      <p className="text-2xl font-black bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 bg-clip-text text-transparent">
                        ₹{course.price.toLocaleString("en-IN")}
                      </p>

                      <button
                        onClick={() => handleEnroll(course)}
                        className={`px-8 py-3 ${glossyRedButton}`}
                      >
                        Enroll
                      </button>

                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
